/**
 * TOUR-END OUTCOME (agent-led demos slice 4) — the media-ended analog
 * for tours. A tour reaching its end (or its operator-marked completion
 * step — the story-landed moment by definition) is the point of highest
 * intent and, before this, dead air: nothing told the agent. Now the
 * player's `completed` bridge event becomes the agent's cue to LAND the
 * story — outcome in a line, then the arc's CTA / an offer to book / a
 * question. Once per tour per page-life; only when the tour is actually
 * the agent's business (their stage, their content pane, or a live
 * call) — a self-play launcher with no agent stays silent.
 */

export type TourEndRetakeDeps = {
  /** Is this tour the agent's business right now? (presenter mount,
      the content pane's active tour, or a live voice call). */
  isRelevant: (tourPublicId: string) => boolean;
  /** Live call → Daily app message; false when no call is up (the
      caller falls back to the hidden text nudge). */
  notifyVoice: () => boolean;
  /** Text mode — one hidden nudge; the agent decides how to land. */
  sendNudge: () => void;
  /** Slice 5: persist the completion to the SESSION plane (one
      content-completed per relevant tour) — the demo-watched rep alert
      and the Conversations inbox read it. Fires before the retake
      routing, voice or text alike. */
  record?: (tourPublicId: string) => void;
  /** The completion is the AGENT'S OWN doing — its next/goTo put the
      stage on the final screen (stage truth remembers; the player's
      `completed` only fires when that last step ENDS, so no time window
      can tell). The tourControl result it read already carries the
      land-it cue, so a nudge on top made it close twice (2026-09-21
      smoke, twice). The completion is still recorded; only the cue is
      skipped. */
  agentDrove?: (tourPublicId: string) => boolean;
};

/** The hidden text nudge. The stance lives in the presenter block; this
    only marks the moment (freewill law — a cue, never a script). */
export const TOUR_END_NUDGE =
  '(the tour just reached its end — the story is told and the viewer is back with you. Land it: one or two lines on the outcome it showed, then the natural next step — the CTA if the moment is right, an offer to book time, or a question. Never recap screen by screen, never restart the tour unasked.)';

export type TourEndRetake = {
  /** The player's `completed` bridge event for a tour. */
  onCompleted(tourPublicId: string): void;
};

export function createTourEndRetake(deps: TourEndRetakeDeps): TourEndRetake {
  const landed = new Set<string>();
  return {
    onCompleted(tourPublicId: string): void {
      if (!tourPublicId || landed.has(tourPublicId)) return;
      if (!deps.isRelevant(tourPublicId)) return;
      landed.add(tourPublicId);
      deps.record?.(tourPublicId);
      if (deps.agentDrove?.(tourPublicId)) return;
      if (deps.notifyVoice()) return;
      deps.sendNudge();
    },
  };
}
